import { useApiMutation } from "@/packages/features-shared";
import type {
  AuditDashboardMetrics,
  UIAudit,
  UIAuditCategory,
} from "@/packages/types";
import { useTranslation } from "@/packages/utils";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { api } from "convex/_generated/api";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AISummaryCard } from "./AISummaryCard";
import { AuditMetadataCard } from "./AuditMetadataCard";
import { AuditProgressCard } from "./AuditProgressCard";
import { AuditSummaryDetailsSheet } from "./AuditSummaryDetailsSheet";
import { DeadlineCard } from "./DeadlineCard";
import { FindingsCard } from "./FindingsCard";
import { GetStartedCard } from "./GetStartedCard";
import { RecentChecklistCard } from "./RecentChecklistCard";
import { ReportActionCard } from "./ReportActionCard";
import { StatusTimelineCard } from "./StatusTimelineCard";

interface AuditOverviewTabProps {
  audit: UIAudit;
  categories: UIAuditCategory[];
  dashboardMetrics?: AuditDashboardMetrics;
  onEditTags?: () => void;
  onUploadDocument?: () => void;
  onEditAudit?: () => void;
  onOpenReport?: () => void;
}

export function AuditOverviewTab({
  audit,
  categories,
  dashboardMetrics: m,
  onEditTags,
  onUploadDocument: _onUploadDocument,
  onEditAudit,
  onOpenReport,
}: AuditOverviewTabProps) {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });
  const [selectedSummarySheetOpen, setSelectedSummarySheetOpen] =
    useState(false);

  const { t } = useTranslation(["audits"]);

  const { data: summaryStatus } = useWorkspaceQuery(
    api.audit_queries.getAuditSummaryStatus,
    { auditId: audit.id },
  );
  const isGenerating = summaryStatus?.isGenerating ?? false;
  const isUpToDate = summaryStatus?.isUpToDate ?? false;

  // Track generation state to show toast when generation completes
  const wasGeneratingRef = useRef(false);
  useEffect(() => {
    if (isGenerating) {
      wasGeneratingRef.current = true;
      return;
    }
    if (wasGeneratingRef.current) {
      wasGeneratingRef.current = false;
      if (isUpToDate) {
        toast.info(t("audits:overview.aiSummary.alreadyUpToDate"));
      } else if (summaryStatus?.hasSummary) {
        toast.success(t("audits:overview.aiSummary.updated"));
      }
    }
  }, [isGenerating, isUpToDate, summaryStatus?.hasSummary, t]);

  const generateSummaryMutation = useApiMutation(
    api.audit_mutations.generateAuditSummary,
    {
      name: "generate-audit-summary",
    },
  );

  const handleOpenSummaryDetails = () => {
    if (audit.aiSummaryData) {
      setSelectedSummarySheetOpen(true);
    }
  };

  const handleGenerateSummary = () => {
    generateSummaryMutation.mutateAsync({
      auditId: audit.id,
    });
  };

  // Derived data
  const checklistStats = m?.checklistStats ?? {
    total: 0,
    completed: 0,
    inProgress: 0,
    notStarted: 0,
  };
  const documentStats = m?.documentStats ?? {
    total: 0,
    reviewed: 0,
    pending: 0,
    flagged: 0,
  };
  const isTerminal = audit.status === "archived" || audit.status === "rejected";

  // Navigation helpers
  const onNavigateToCategory = (categoryId: string) => {
    navigate({
      to: "/$workspaceSlug/audits/$auditId",
      params: { workspaceSlug: workspaceSlug ?? "", auditId: audit.id },
      search: { tab: "checklist", categories: categoryId },
    });
  };

  const onNavigateToItem = (itemId: string) => {
    navigate({
      to: "/$workspaceSlug/audits/$auditId",
      params: { workspaceSlug: workspaceSlug ?? "", auditId: audit.id },
      search: { tab: "checklist", checklistItemId: itemId },
    });
  };

  const onNavigateToReport = () => {
    if (onOpenReport) {
      onOpenReport();
    } else {
      navigate({
        to: "/$workspaceSlug/audits/$auditId",
        params: { workspaceSlug: workspaceSlug ?? "", auditId: audit.id },
        search: { tab: "overview", openReport: true },
      });
    }
  };

  const onGoToFindings = () => {
    navigate({
      to: "/$workspaceSlug/audits/$auditId",
      params: { workspaceSlug: workspaceSlug ?? "", auditId: audit.id },
      search: { tab: "findings" },
    });
  };

  const onGoToChecklist = () => {
    navigate({
      to: "/$workspaceSlug/audits/$auditId",
      params: { workspaceSlug: workspaceSlug ?? "", auditId: audit.id },
      search: { tab: "checklist" },
    });
  };

  const showGetStartedCard =
    checklistStats.completed === 0 && documentStats.total === 0;

  return (
    <div className="flex-1 overflow-y-auto pr-1">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 grid-flow-dense">
        {/* Row 1 */}
        <AuditProgressCard
          categories={categories}
          checklistStats={checklistStats}
          onNavigateToCategory={onNavigateToCategory}
          index={0}
          variant="elevated"
        />
        <DeadlineCard
          audit={audit}
          categories={categories}
          index={1}
          variant="elevated"
        />

        {/* Row 2 */}
        <FindingsCard
          findingStats={m?.findingStats}
          onNavigateToFindings={onGoToFindings}
          index={2}
        />
        <AISummaryCard
          audit={audit}
          isGenerating={isGenerating}
          onOpenSheet={handleOpenSummaryDetails}
          onGenerate={handleGenerateSummary}
          isTerminal={isTerminal}
          index={3}
          variant="accent"
        />
        <StatusTimelineCard audit={audit} index={4} />

        {/* Row 3 */}
        <AuditMetadataCard
          audit={audit}
          onEditAudit={onEditAudit}
          onEditTags={onEditTags}
          index={5}
        />
        <ReportActionCard
          auditId={audit.id}
          onNavigateToReport={onNavigateToReport}
          index={6}
        />
        {showGetStartedCard && (
          <GetStartedCard
            checklistTotal={checklistStats.total}
            documentTotal={documentStats.total}
            auditId={audit.id}
            onGoToChecklist={onGoToChecklist}
            index={7}
          />
        )}

        {/* Row 4 */}
        <RecentChecklistCard
          categories={categories}
          onNavigateToItem={onNavigateToItem}
          onGoToChecklist={onGoToChecklist}
          index={8}
        />
      </div>

      {/* AI Summary Details Sheet */}
      <AuditSummaryDetailsSheet
        aiData={audit.aiSummaryData}
        open={selectedSummarySheetOpen}
        onClose={() => setSelectedSummarySheetOpen(false)}
        onGenerate={handleGenerateSummary}
        isGenerating={isGenerating}
        isUpToDate={isUpToDate}
      />
    </div>
  );
}
